import { defineMessages } from 'react-intl';

type MessageKeys = 'headingForPrompts' | 'descriptionForPrompts';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		headingForPrompts: {
			id: 'rovo-chat.rovo-onboarding-prompts.heading',
			defaultMessage: 'What do you want to use Rovo for today?',
			description: 'Intro header message for Rovo Chat Onboarding Prompts flow.',
		},
		descriptionForPrompts: {
			id: 'rovo-chat.rovo-onboarding-prompts.description',
			defaultMessage: 'Pick one to personalize your experience. You can always explore more later.',
			description: 'Intro description message for Rovo Chat Onboarding flow.',
		},
	});

export default message;

export const CREATOR_STUBS: {
	createNewIssuesQuicklyStub: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	breakDownComplexIssuesStub: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	makeMyIssuesClearerStub: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	createNewIssuesQuicklyStub: {
		id: 'rovo-chat.creator-prompts.create-new-issues-quickly',
		defaultMessage: 'Create new issues quickly',
		description: 'Prompt for creating a new issue quickly for Rovo Chat Onboarding flow.',
	},
	breakDownComplexIssuesStub: {
		id: 'rovo-chat.creator-prompts.break-down-complex-issues',
		defaultMessage: 'Break down complex issues',
		description: 'Prompt for breaking down complex issues for Rovo Chat Onboarding flow.',
	},
	makeMyIssuesClearerStub: {
		id: 'rovo-chat.creator-prompts.make-my-issues-clearer',
		defaultMessage: 'Make my issues clearer',
		description: 'Prompt for making my issues clearer for Rovo Chat Onboarding flow.',
	},
});

export const CONSUMER_STUBS: {
	catchupAfterTimeAwayStub: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	draftHelpfulCommentsOrUpdatesStub: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	shareMyWorkWithStakeholdersStub: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	catchupAfterTimeAwayStub: {
		id: 'rovo-chat.consumer-prompts.catchup-after-time-away',
		defaultMessage: 'Catch up after time away',
		description: 'Prompt for catching up after time away for Rovo Chat Onboarding flow.',
	},
	draftHelpfulCommentsOrUpdatesStub: {
		id: 'rovo-chat.consumer-prompts.draft-helpful-comments-or-updates',
		defaultMessage: 'Draft helpful comments or updates',
		description: 'Prompt for drafting helpful comments or updates for Rovo Chat Onboarding flow.',
	},
	shareMyWorkWithStakeholdersStub: {
		id: 'rovo-chat.consumer-prompts.share-my-work-with-stakeholders',
		defaultMessage: 'Share my work with stakeholders',
		description: 'Prompt for sharing my work with stakeholders for Rovo Chat Onboarding flow.',
	},
});

export const ALL_PROMPTS: {
	createNewIssuesQuicklyPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	breakDownComplexIssuesPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	makeMyIssuesClearerPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	catchupAfterTimeAwayPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	draftHelpfulCommentsOrUpdatesPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	shareMyWorkWithStakeholdersPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	catchupAfterTimeAwayConfluencePrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	draftHelpfulCommentsOrUpdatesConfluencePrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	shareMyWorkWithStakeholdersConfluencePrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	createNewIssuesQuicklyPrompt: {
		id: 'rovo-chat.creator-prompts.create-new-issues-quickly-prompt',
		defaultMessage:
			'Suggest a few concrete issues or subtasks my team might be missing. Start with a brief overview of your reasoning, then list 3 work items, each with a clear title and one‑line description. Keep response under 300 words.',
		description: 'Prompt for creating new issues quickly for Rovo Chat Onboarding flow.',
	},
	breakDownComplexIssuesPrompt: {
		id: 'rovo-chat.creator-prompts.break-down-complex-issues-prompt',
		defaultMessage:
			'Identify my team’s most complex work item, and suggest 3 subtasks to track separately. Start with a brief overview of your reasoning, then list 3 subtasks, each with a clear title and one‑line description. Keep response under 300 words.',
		description: 'Prompt for breaking down complex issues for Rovo Chat Onboarding flow.',
	},
	makeMyIssuesClearerPrompt: {
		id: 'rovo-chat.creator-prompts.make-my-issues-clearer-prompt',
		defaultMessage:
			'Identify an issue that seems unclear, outdated, or hard to act on. Start with a brief overview of your reasoning, then share 3 concrete ideas to help my team improve, progress, or close it quickly and confidently. Keep response under 300 words.',
		description: 'Prompt for making my issues clearer for Rovo Chat Onboarding flow.',
	},
	catchupAfterTimeAwayPrompt: {
		id: 'rovo-chat.consumer-prompts.catchup-after-time-away-prompt',
		defaultMessage:
			'Based on the work items you can see, give me a high-level summary of what’s changed over the past two weeks. Focus on items that changed, are delayed or blocked, and anything else that needs my attention. Keep response under 300 words.',
		description: 'Prompt for catching up after time away for Rovo Chat Onboarding flow.',
	},
	draftHelpfulCommentsOrUpdatesPrompt: {
		id: 'rovo-chat.consumer-prompts.stay-on-top-of-my-work-prompt',
		defaultMessage:
			'Identify an important or recently updated work item. Start with a brief overview of your reasoning, then draft a succinct and friendly comment I can post to the team. Include key questions, flagged risks, and clear next steps. Keep response under 300 words.',
		description: 'Prompt for drafting helpful comments or updates for Rovo Chat Onboarding flow.',
	},
	shareMyWorkWithStakeholdersPrompt: {
		id: 'rovo-chat.consumer-prompts.prep-for-meeting-prompt',
		defaultMessage:
			'Identify an important or recently updated work item. Start with a brief overview of your reasoning, then write a short memo that I can share with stakeholders. Include a one-sentence overview and a few bullets for progress, risks, and next steps. Keep the response under 300 words.',
		description: 'Prompt for sharing my work with stakeholders for Rovo Chat Onboarding flow.',
	},
	catchupAfterTimeAwayConfluencePrompt: {
		id: 'rovo-chat.consumer-prompts.confluence.catchup-after-time-away-prompt',
		defaultMessage:
			'Based on the Confluence Pages related to me, give me a high-level summary of what’s changed over the past two weeks. Focus on pages that have been created, changed, and anything else that needs my attention. Keep response under 300 words.',
		description: 'Prompt for catching up after time away for Rovo Chat Onboarding flow.',
	},
	draftHelpfulCommentsOrUpdatesConfluencePrompt: {
		id: 'rovo-chat.consumer-prompts.confluence.stay-on-top-of-my-work-prompt',
		defaultMessage:
			'Identify an important or recently updated Confluence Page. Start with a brief overview of your reasoning, then draft a succinct and friendly comment I can post to the team. Include key questions, flagged risks, and clear next steps. Keep response under 300 words.',
		description: 'Prompt for drafting helpful comments or updates for Rovo Chat Onboarding flow.',
	},
	shareMyWorkWithStakeholdersConfluencePrompt: {
		id: 'rovo-chat.consumer-prompts.confluence.prep-for-meeting-prompt',
		defaultMessage:
			'Identify an important or recently updated Confluence Page. Start with a brief overview of your reasoning, then write a short memo that I can share with stakeholders. Include a one-sentence overview and a few bullets for progress, risks, and next steps. Keep the response under 300 words.',
		description: 'Prompt for sharing my work with stakeholders for Rovo Chat Onboarding flow.',
	},
});
